/**
 * Historique des mouvements d'un GRV : entrées et sorties de géozone.
 *
 * Le premier mouvement observé pour un GRV n'a pas de zone quittée : aucune zone précédente n'est
 * connue, et l'afficher comme "—" plutôt que de l'inventer est le comportement correct.
 *
 * Un mouvement ne porte qu'un seul horodatage côté API (`mouvements/models.py`) : c'est l'instant où
 * le GRV franchit la limite, à la fois sortie de la zone quittée et entrée dans la zone d'entrée. Une
 * colonne "Horodatage" générique laisserait deviner à quel côté du mouvement elle se rapporte — on
 * l'affiche donc deux fois, chacune sous une colonne explicitement liée à sa zone, plutôt qu'une fois
 * sous un intitulé ambigu.
 */
import { useMemo } from "react";

import { Chargement, Erreur, Localisation, Vide } from "../../shared/composants/Etats";
import { EnTeteTriable, useTrie, useTriTableau } from "../../shared/composants/tableauTriable";
import { formaterDate, formaterDureeJours } from "../../shared/composants/formatage";
import type { Mouvement } from "../../shared/api/types";
import { useMouvementsDuGrv } from "./requetes";

type Colonne = "zone_quittee_nom" | "sortie" | "duree" | "zone_entree_nom" | "entree";

function valeurDe(mouvement: Mouvement, colonne: Colonne): string | number | null | undefined {
  switch (colonne) {
    case "zone_quittee_nom":
      return mouvement.zone_quittee_nom;
    case "zone_entree_nom":
      return mouvement.zone_entree_nom;
    case "duree":
      return mouvement.duree_presence_minutes;
    case "sortie":
    case "entree":
      return mouvement.timestamp;
  }
}

export function OngletMouvements({ numero }: { numero: string }) {
  const requete = useMouvementsDuGrv(numero);
  const { tri, basculerTri } = useTriTableau<Colonne>({ colonne: "sortie", descendant: true });

  const donnees = useMemo(() => requete.data?.data ?? [], [requete.data]);
  const triees = useTrie(donnees, tri, valeurDe);

  if (requete.isPending) return <Chargement libelle="Chargement des mouvements…" />;
  if (requete.isError) return <Erreur erreur={requete.error} reessayer={() => void requete.refetch()} />;
  if (triees.length === 0) return <Vide libelle="Aucun mouvement enregistré." />;

  return (
    <table className="tableau">
      <thead>
        <tr>
          <EnTeteTriable
            colonne="zone_quittee_nom"
            libelle="Zone quittée"
            tri={tri}
            basculerTri={basculerTri}
          />
          <EnTeteTriable
            colonne="sortie"
            libelle="Date/heure de sortie"
            tri={tri}
            basculerTri={basculerTri}
          />
          <EnTeteTriable
            colonne="duree"
            libelle="Durée dans cette zone"
            tri={tri}
            basculerTri={basculerTri}
          />
          <EnTeteTriable
            colonne="zone_entree_nom"
            libelle="Zone d'entrée"
            tri={tri}
            basculerTri={basculerTri}
          />
          <EnTeteTriable
            colonne="entree"
            libelle="Date/heure d'entrée"
            tri={tri}
            basculerTri={basculerTri}
          />
        </tr>
      </thead>
      <tbody>
        {triees.map((mouvement, index) => (
          <tr key={`${mouvement.numero_grv}-${mouvement.timestamp}-${index}`}>
            <td>
              {mouvement.zone_quittee_nom ? (
                <Localisation zone={mouvement.zone_quittee_nom} agregee={mouvement.localisation_agregee} />
              ) : (
                "—"
              )}
            </td>
            <td>{mouvement.zone_quittee_nom ? formaterDate(mouvement.timestamp) : "—"}</td>
            <td>
              {mouvement.duree_presence_minutes != null
                ? formaterDureeJours(mouvement.duree_presence_minutes)
                : "—"}
            </td>
            <td>
              <Localisation zone={mouvement.zone_entree_nom} agregee={mouvement.localisation_agregee} />
            </td>
            <td>{formaterDate(mouvement.timestamp)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
